



import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { LeftSidebar } from './components/LeftSidebar.tsx';
import { CommandPalette } from './components/CommandPalette.tsx';
import { StatusBar } from './components/StatusBar.tsx';
import { FeaturePalette } from './components/FeaturePalette.tsx';
import { TerminalIcon, CpuChipIcon, HomeIcon, MagnifyingGlassIcon, Squares2x2Icon, CodeBracketSquareIcon, ArchiveBoxIcon } from './components/icons/InterfaceIcons.tsx';
import type { ViewType, Feature, FeatureId, ChromeViewType } from './types.ts';
import { FEATURES_MAP, ALL_FEATURES } from './components/features/index.ts';
import { Placeholder } from './components/features/Placeholder.tsx';
import { MachineView } from './components/MachineView.tsx';
import { FeatureGrid } from './components/FeatureGrid.tsx';
import { useGlobalState } from './contexts/GlobalStateContext.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { logEvent } from './services/telemetryService.ts';

const chromeViewComponentMap: Record<string, React.FC<any>> = {
    'home': MachineView,
    'features-list': FeatureGrid,
};

const LoadingIndicator: React.FC = () => (
    <div className="w-full h-full flex items-center justify-center">
        <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0s' }}></div>
            <div className="w-4 h-4 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-4 h-4 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            <span className="text-slate-400 ml-2">Loading Feature...</span>
        </div>
    </div>
);


const App: React.FC = () => {
  const { state, dispatch } = useGlobalState();
  const { activeView } = state;
  const [isCommandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [isFocusMode, setFocusMode] = useState(false);

  const handleViewChange = useCallback((view: ViewType) => {
    dispatch({ type: 'SET_VIEW', payload: view });
    logEvent('view_changed', { view });
    setFocusMode(false); // Exit focus mode when changing views
  }, [dispatch]);

  const toggleCommandPalette = useCallback(() => {
    setCommandPaletteOpen(prev => !prev);
  }, []);

  const toggleFocusMode = useCallback((isFocused: boolean) => {
    setFocusMode(isFocused);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggleCommandPalette();
      }
      if (e.key === 'Escape') {
        setCommandPaletteOpen(false);
        setFocusMode(false); // Also allow Esc to exit focus mode
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleCommandPalette]);

  const { ActiveComponent, activeFeature } = useMemo(() => {
    const feature = FEATURES_MAP.get(activeView as FeatureId);

    if (feature) {
      return {
        ActiveComponent: feature.component,
        activeFeature: feature,
      };
    }
    
    // If it's not a feature, it might be a chrome view
    const chromeViewComponent = chromeViewComponentMap[activeView as ChromeViewType];
    if (chromeViewComponent) {
        return {
            ActiveComponent: chromeViewComponent,
            activeFeature: undefined,
        };
    }

    // Fallback to placeholder
    return {
      ActiveComponent: () => <Placeholder />,
      activeFeature: undefined,
    };
  }, [activeView]);


  const sidebarItems = [
    { id: 'ai-feature-builder', icon: <CodeBracketSquareIcon />, label: 'AI Builder', view: 'ai-feature-builder' },
    { id: 'python-repl', icon: <CpuChipIcon />, label: 'Python REPL', view: 'python-repl' },
    { id: 'home', icon: <HomeIcon />, label: 'Machine', view: 'home' },
    { id: 'features-list', icon: <Squares2x2Icon />, label: 'Features List', view: 'features-list' },
    { id: 'ai-code-explainer', icon: <CpuChipIcon />, label: 'AI Explainer', view: 'ai-code-explainer' },
    { id: 'content-manager', icon: <ArchiveBoxIcon />, label: 'Content Manager', view: 'content-manager' },
    { id: 'search', icon: <MagnifyingGlassIcon />, label: 'Search', action: toggleCommandPalette },
  ];

  return (
    <div className="flex h-screen w-screen font-sans bg-slate-900 text-slate-300 overflow-hidden fade-in">
      <CommandPalette isOpen={isCommandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} onSelect={(view) => {
          handleViewChange(view);
          setCommandPaletteOpen(false);
      }}/>
      {!isFocusMode && <LeftSidebar items={sidebarItems} activeView={activeView} onNavigate={handleViewChange} />}
      <div className="flex flex-col flex-1 min-w-0">
        <main className="flex-1 overflow-y-auto">
          <ErrorBoundary>
            <Suspense fallback={<LoadingIndicator />}>
              {activeView === 'features-list'
                ? <FeatureGrid features={ALL_FEATURES} onFeatureSelect={handleViewChange} />
                : <ActiveComponent feature={activeFeature} toggleFocusMode={toggleFocusMode} onNavigate={handleViewChange} />
              }
            </Suspense>
          </ErrorBoundary>
        </main>
        {!isFocusMode && <StatusBar />}
      </div>
      {!isFocusMode && <FeaturePalette onFeatureSelect={handleViewChange} />}
    </div>
  );
};

export default App;